import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type UserProfile = NonNullable<
  components['schemas']['ProfileResponse']['data']
>;

export type UpdateFplTeamIdResult =
  | { ok: true; profile: UserProfile }
  | { ok: false; status: number; message: string };

export type CreateProfileInput = {
  email?: string;
};

const NETWORK_ERROR_STATUS = 0;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

/**
 * Fetches the logged-in user's profile. Returns `null` when the backend has no
 * profile for this user yet (404) — signup creates one via `getOrCreateProfile`.
 */
export const fetchProfile = async (
  accessToken: string
): Promise<UserProfile | null> => {
  const { data, error, response } = await ffhClient().GET('/users/me', {
    headers: authHeader(accessToken)
  });

  if (response?.status === HTTP_NOT_FOUND) return null;
  if (error) throw new Error('Failed to load user profile');
  return data?.data ?? null;
};

/** Creates the logged-in user's profile with their signup details. */
export const createProfile = async (
  accessToken: string,
  input: CreateProfileInput
): Promise<UserProfile> => {
  const { data, error, response } = await ffhClient().POST('/users/me', {
    headers: authHeader(accessToken),
    body: { email: input.email }
  });

  // A concurrent create won the race — the profile now exists, so fetch it.
  if (response?.status === HTTP_CONFLICT) {
    const existing = await fetchProfile(accessToken);
    if (existing) return existing;
  }
  if (error || !data?.data) throw new Error('Failed to create user profile');
  return data.data;
};

/**
 * Fetches the user's profile, creating it if it does not exist yet. The GET
 * runs first (returning users already have a profile); only a 404 triggers the
 * create with the user's email.
 *
 * On the create path (brand-new signup) an optional `refreshAccessToken` is
 * called first to force a fresh token, so client-visible custom claims —
 * notably `profileId`, which Auth0 assigns only once the backend user exists —
 * are up to date for downstream identify/analytics (e.g. CleverTap). Falls back
 * to the existing token if the refresh is unavailable, returns null, or throws.
 */
export const getOrCreateProfile = async (
  accessToken: string,
  input: CreateProfileInput,
  refreshAccessToken?: () => Promise<string | null>
): Promise<UserProfile> => {
  const existing = await fetchProfile(accessToken);
  if (existing) return existing;

  let freshToken = accessToken;
  if (refreshAccessToken) {
    try {
      freshToken = (await refreshAccessToken()) ?? accessToken;
    } catch (error) {
      console.error('Token refresh before profile create failed:', error);
    }
  }
  return createProfile(freshToken, input);
};

export type UpdateProfileFields = {
  fplTeamId: number | null;
  /**
   * Set-only on the backend: omit to leave the stored mini-league untouched
   * (it cannot be cleared through this endpoint).
   */
  miniLeagueId?: number;
};

export const updateProfileFields = async (
  accessToken: string,
  fields: UpdateProfileFields,
  signal?: AbortSignal
): Promise<UpdateFplTeamIdResult> => {
  const { data, error, response } = await ffhClient().PUT('/users/me', {
    headers: authHeader(accessToken),
    body: fields,
    signal
  });

  if (error || !data?.data) {
    return {
      ok: false,
      status: response?.status ?? NETWORK_ERROR_STATUS,
      message: 'Failed to update profile'
    };
  }

  return { ok: true, profile: data.data };
};

export const updateFplTeamId = async (
  accessToken: string,
  fplTeamId: number | null,
  signal?: AbortSignal
): Promise<UpdateFplTeamIdResult> =>
  updateProfileFields(accessToken, { fplTeamId }, signal);
